import {
  Accordion,
  Box,
  Button,
  Card,
  Container,
  Group,
  SimpleGrid,
  Stack,
  Text,
  ThemeIcon,
  Title,
  useMantineTheme,
} from "@mantine/core";
import {
  IconArrowRight,
  IconCheck,
  IconChevronDown,
  IconChevronUp,
} from "@tabler/icons-react";
import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import AssignmentHelpHowItWorks from "../Shared/AssignmentHelpHowItWorks";
import AssignmentSupportCta from "../Shared/AssignmentSupportCta";
import assignmentHelpCatalog from "../../data/assignmentHelpCatalog.json";
import Seo from "../../seo/Seo";
import {
  buildCanonicalUrl,
  createBreadcrumbSchema,
  createFaqSchema,
  createOrganizationSchema,
} from "../../seo/utils";
import { getAssignmentPagePath } from "../../utils/routes";
import "./AssignmentHelp.css";

interface CatalogPage {
  label?: string;
  slug?: string;
  title?: string;
  metaDescription?: string;
  intro?: string;
}

interface CatalogCategory {
  title?: string;
  description?: string;
  cta?: string;
  pages?: CatalogPage[];
}

const assignmentCatalog = assignmentHelpCatalog as CatalogCategory[];
const pageTitle = "Assignment Help Services Online | SubjectBuddy";
const pageDescription =
  "Explore SubjectBuddy assignment help services for business, law, management, programming, computer science, nursing, statistics, engineering, essays, reports, case studies, and more.";
const pagePath = "/assignment-help";
const fallbackServiceDescription =
  "Get subject-specific academic support for this assignment area.";

const preferredPopularSlugs = [
  "business-assignment-help",
  "management-assignment-help",
  "law-assignment-help",
  "computer-science-assignment-help",
  "python-programming-assignment-help",
  "java-assignment-help",
  "sql-assignment-help",
  "statistics-assignment-help",
  "nursing-assignment-help",
  "engineering-assignment-help",
  "accounting-assignment-help",
  "marketing-assignment-help",
];

const servicesLandingContent = {
  hero: {
    title: "Assignment Help Services for University Students",
    description:
      "Get subject-specific academic support for essays, reports, case studies, coding tasks, data analysis, presentations, research work, and technical coursework. SubjectBuddy helps students understand their brief, plan their work, improve structure, and prepare more confident submissions.",
    badges: [
      "Subject-specific guidance",
      "Urgent deadline support",
      "Essays, reports, coding, and data tasks",
      "Rubric-focused academic support",
    ],
  },
  seoIntro: [
    "SubjectBuddy provides assignment help and online assignment help for university students who need clearer guidance before submission. Our academic assignment support is built around brief interpretation, coursework planning, structure improvement, concept explanation, editing support, and draft improvement.",
    "Students use SubjectBuddy for subject-specific assignment help across business assignment help, programming assignment help, nursing assignment help, statistics assignment help, law, management, computer science, engineering, data analysis, essays, reports, and case studies. The focus is practical learning support: understanding the task, improving the work you have, and making stronger academic choices.",
  ],
  popularServices: {
    title: "Popular Assignment Help Services",
    description:
      "Start with high-demand subject areas, then open the service page that best matches your coursework.",
  },
  categories: {
    title: "Explore Assignment Help by Subject",
    description:
      "Browse SubjectBuddy services by academic area and choose the support that best matches your assignment.",
  },
  assignmentTypes: [
    "Essays",
    "Reports",
    "Case studies",
    "Presentations",
    "Literature reviews",
    "Research proposals",
    "Dissertation chapters",
    "Reflective writing",
    "Coding assignments",
    "Database assignments",
    "Data analysis tasks",
    "Lab reports",
    "Technical documentation",
    "Business reports",
    "Statistical analysis",
    "Coursework improvement",
  ],
  whyChoose: [
    {
      title: "Subject-specific academic support",
      description:
        "Get guidance matched to the subject area, assignment type, and university expectations.",
    },
    {
      title: "Clear explanations",
      description:
        "Break down concepts, frameworks, methods, code, or calculations into manageable next steps.",
    },
    {
      title: "Brief and rubric-focused help",
      description:
        "Keep the work aligned with the question, marking criteria, required format, and deadline.",
    },
    {
      title: "Technical and non-technical subjects",
      description:
        "Find support for essays and reports as well as programming, databases, statistics, and lab work.",
    },
    {
      title: "Feedback-led improvement",
      description:
        "Use targeted feedback to improve structure, clarity, evidence, analysis, and presentation.",
    },
    {
      title: "Student-friendly communication",
      description:
        "Share what you need in plain language and get practical guidance that feels easy to act on.",
    },
  ],
  mainFaqs: [
    {
      question: "What subjects does SubjectBuddy support?",
      answer:
        "SubjectBuddy supports business, law, management, programming, computer science, data analysis, statistics, engineering, science, humanities, social sciences, and many other university assignment areas listed on this page.",
    },
    {
      question: "Can I get urgent assignment help?",
      answer:
        "Yes, urgent assignment help is available depending on the deadline and complexity. Share your subject, brief, marking rubric, deadline, and current progress so the support can focus on the most important next steps.",
    },
    {
      question: "Can you help with my marking rubric?",
      answer:
        "Yes. You can share your marking rubric so the guidance can focus on the required structure, evidence, analysis, formatting, referencing, and assessment criteria.",
    },
    {
      question: "Do you help with essays, reports, and case studies?",
      answer:
        "Yes. SubjectBuddy can help with essay planning, report structure, case study analysis, argument flow, source use, referencing guidance, and draft improvement.",
    },
    {
      question: "Can you help with programming and technical assignments?",
      answer:
        "Yes. Support is available for programming, databases, SQL, Python, Java, data analysis, computer science, statistics, engineering, lab reports, and technical documentation.",
    },
    {
      question: "Can you help improve my existing draft?",
      answer:
        "Yes. You can share your draft for feedback on structure, clarity, logic, evidence use, technical explanation, paragraph flow, referencing, and final preparation.",
    },
    {
      question: "What should I share before requesting assignment help?",
      answer:
        "Share your subject, assignment brief, marking rubric, deadline, word count, referencing style, current draft or notes, and the exact part where you need support.",
    },
    {
      question: "Is SubjectBuddy suitable for university students?",
      answer:
        "Yes. SubjectBuddy is designed for university students who need academic guidance, concept explanation, coursework planning, draft improvement, and subject-specific support before submission.",
    },
  ],
};

function createHeadingAnchor(value: string) {
  return String(value || "")
    .toLowerCase()
    .trim()
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function titleCase(value: string) {
  const acronyms: Record<string, string> = {
    ajax: "AJAX",
    anova: "ANOVA",
    cmi: "CMI",
    dbms: "DBMS",
    gis: "GIS",
    myob: "MYOB",
    php: "PHP",
    sql: "SQL",
    spss: "SPSS",
    uml: "UML",
  };

  return value
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => {
      const normalizedWord = word.toLowerCase();

      if (acronyms[normalizedWord]) {
        return acronyms[normalizedWord];
      }

      return `${normalizedWord.charAt(0).toUpperCase()}${normalizedWord.slice(1)}`;
    })
    .join(" ");
}

function getPageTitle(page: CatalogPage) {
  return page.title?.trim() || titleCase(page.label?.trim() || "Assignment Help");
}

function getPageDescription(page: CatalogPage) {
  return page.metaDescription?.trim() || page.intro?.trim() || fallbackServiceDescription;
}

function getAssignmentHref(slug: string) {
  return getAssignmentPagePath(slug);
}

function getCatalogPages(category: CatalogCategory) {
  return Array.isArray(category.pages)
    ? category.pages.filter((page) => Boolean(page.slug?.trim()))
    : [];
}

function getAllCatalogPages() {
  return assignmentCatalog.flatMap((category) => getCatalogPages(category));
}

function getPopularServices() {
  const pages = getAllCatalogPages();

  return preferredPopularSlugs
    .map((slug) => pages.find((page) => page.slug === slug))
    .filter((page): page is CatalogPage => Boolean(page));
}

function AssignmentHelpIndexPage() {
  const theme = useMantineTheme();
  const { hash } = useLocation();
  const [expandedCategories, setExpandedCategories] = useState<Record<string, boolean>>({});
  const hasCatalog = assignmentCatalog.length > 0;
  const popularServices = getPopularServices();
  const seoIntroId = createHeadingAnchor(
    "Online Assignment Help That Starts With Your Brief",
  );
  const popularServicesId = createHeadingAnchor(
    servicesLandingContent.popularServices.title,
  );
  const categoriesId = createHeadingAnchor(servicesLandingContent.categories.title);
  const assignmentTypesId = createHeadingAnchor(
    "Assignment Types We Support",
  );
  const whyChooseId = createHeadingAnchor("Why Choose SubjectBuddy");
  const faqId = createHeadingAnchor("Assignment Help FAQs");
  const getHelpId = createHeadingAnchor("Get Assignment Help");
  const sectionLinks = [
    {
      href: `#${seoIntroId}`,
      label: "How It Starts",
    },
    ...(hasCatalog
      ? [
          {
            href: `#${popularServicesId}`,
            label: "Popular Services",
          },
          {
            href: `#${categoriesId}`,
            label: "Explore by Subject",
          },
        ]
      : []),
    {
      href: `#${assignmentTypesId}`,
      label: "Assignment Types",
    },
    {
      href: `#${whyChooseId}`,
      label: "Why Choose SubjectBuddy",
    },
    {
      href: `#${faqId}`,
      label: "FAQs",
    },
    {
      href: `#${getHelpId}`,
      label: "Get Help",
    },
  ];

  const toggleCategory = (categoryKey: string) => {
    setExpandedCategories((current) => ({
      ...current,
      [categoryKey]: !current[categoryKey],
    }));
  };

  useEffect(() => {
    if (!hash) {
      return;
    }

    window.requestAnimationFrame(() => {
      const target = document.getElementById(hash.replace(/^#/, ""));

      if (target) {
        target.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    });
  }, [hash]);

  return (
    <main className="assignment-landing">
      <Seo
        title={pageTitle}
        description={pageDescription}
        path={pagePath}
        ogTitle={pageTitle}
        ogDescription={pageDescription}
        schema={[
          createOrganizationSchema(),
          {
            "@context": "https://schema.org",
            "@type": "WebPage",
            name: pageTitle,
            description: pageDescription,
            url: buildCanonicalUrl(pagePath),
            isPartOf: {
              "@type": "WebSite",
              name: "SubjectBuddy",
              url: buildCanonicalUrl("/"),
            },
            inLanguage: "en-GB",
          },
          createBreadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Assignment Help Services", path: pagePath },
          ]),
          createFaqSchema(servicesLandingContent.mainFaqs),
        ]}
      />

      <Box component="section" className="assignment-hero-section">
        <Container size="xl">
          <Stack gap="xl" className="assignment-hero-content">
            <Stack gap="md" maw={920}>
              <Text tt="uppercase" fw={800} size="sm" c={theme.colors.green[8]}>
                SubjectBuddy assignment support
              </Text>
              <Title order={1} c={theme.colors.green[9]} className="assignment-hero-title">
                {servicesLandingContent.hero.title}
              </Title>
              <Text size="lg" c="dimmed" className="assignment-hero-copy">
                {servicesLandingContent.hero.description}
              </Text>
            </Stack>

            <Group gap="md">
              <Button
                component={Link}
                to="/contact"
                size="lg"
                radius="md"
                rightSection={<IconArrowRight size={18} />}
              >
                Get Assignment Help
              </Button>
              <Button
                component="a"
                href={`#${categoriesId}`}
                size="lg"
                radius="md"
                variant="outline"
                color="green"
                rightSection={<IconArrowRight size={18} />}
              >
                Explore Subjects
              </Button>
            </Group>

            <ul className="assignment-trust-badges">
              {servicesLandingContent.hero.badges.map((badge) => (
                <li key={badge}>
                  <IconCheck size={16} />
                  <span>{badge}</span>
                </li>
              ))}
            </ul>
          </Stack>
        </Container>
      </Box>

      <Container size="xl">
        <Stack gap={70}>
          <Box component="section" className="assignment-scroll-target">
            <nav aria-label="Page sections" className="assignment-page-sitelinks">
              <p className="assignment-page-sitelinks-title">On this page</p>
              <ul className="assignment-page-sitelinks-list">
                {sectionLinks.map((sectionLink) => (
                  <li key={sectionLink.href}>
                    <a
                      className="assignment-page-sitelinks-link"
                      href={sectionLink.href}
                    >
                      {sectionLink.label}
                    </a>
                  </li>
                ))}
              </ul>
            </nav>
          </Box>

          <Box
            component="section"
            className="assignment-seo-intro assignment-scroll-target"
            id={seoIntroId}
          >
            <Stack gap="md" maw={980}>
              <Title order={2}>Online Assignment Help That Starts With Your Brief</Title>
              {servicesLandingContent.seoIntro.map((paragraph) => (
                <Text key={paragraph} c="dimmed" className="assignment-section-copy">
                  {paragraph}
                </Text>
              ))}
            </Stack>
          </Box>

          {hasCatalog ? (
            <Box
              component="section"
              aria-labelledby="popular-assignment-services-heading"
              className="assignment-scroll-target"
              id={popularServicesId}
            >
              <Stack gap="lg">
                <Stack gap={6} maw={760}>
                  <Title order={2} id="popular-assignment-services-heading">
                    {servicesLandingContent.popularServices.title}
                  </Title>
                  <Text c="dimmed" className="assignment-section-copy">
                    {servicesLandingContent.popularServices.description}
                  </Text>
                </Stack>

                {popularServices.length ? (
                  <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="lg">
                    {popularServices.map((page) => (
                      <Card
                        key={page.slug}
                        component={Link}
                        to={getAssignmentHref(page.slug || "")}
                        withBorder
                        radius="md"
                        p="lg"
                        className="assignment-popular-card"
                      >
                        <Stack gap="sm" h="100%">
                          <Title order={3}>{getPageTitle(page)}</Title>
                          <Text size="sm" c="dimmed" className="assignment-card-copy">
                            {getPageDescription(page)}
                          </Text>
                          <Text size="sm" fw={800} c="blue" mt="auto">
                            View Service
                          </Text>
                        </Stack>
                      </Card>
                    ))}
                  </SimpleGrid>
                ) : (
                  <Text c="dimmed">Popular services will be available soon.</Text>
                )}
              </Stack>
            </Box>
          ) : null}

          <Box
            component="section"
            className="assignment-scroll-target"
            id={categoriesId}
            aria-labelledby="assignment-services-heading"
          >
            <Stack gap="lg">
              <Stack gap={6} maw={820}>
                <Title order={2} id="assignment-services-heading">
                  {servicesLandingContent.categories.title}
                </Title>
                <Text c="dimmed" className="assignment-section-copy">
                  {servicesLandingContent.categories.description}
                </Text>
              </Stack>

              {hasCatalog ? (
                <div className="assignment-category-grid">
                  {assignmentCatalog.map((category, index) => {
                    const categoryKey = `${category.title || "category"}-${index}`;
                    const pages = getCatalogPages(category);
                    const isExpanded = Boolean(expandedCategories[categoryKey]);
                    const visiblePages = isExpanded ? pages : pages.slice(0, 12);
                    const hasMorePages = pages.length > 12;

                    return (
                      <Card
                        key={categoryKey}
                        withBorder
                        radius="md"
                        p="xl"
                        className="assignment-category-card"
                      >
                        <Stack gap="md" h="100%">
                          <Stack gap={8}>
                            <Title order={3}>
                              {category.title || "Assignment help services"}
                            </Title>
                            {category.description ? (
                              <Text c="dimmed" size="sm" className="assignment-card-copy">
                                {category.description}
                              </Text>
                            ) : null}
                          </Stack>

                          {pages.length ? (
                            <ul className="assignment-service-link-list">
                              {visiblePages.map((page) => (
                                <li key={page.slug}>
                                  <Link
                                    className="assignment-service-link"
                                    to={getAssignmentHref(page.slug || "")}
                                  >
                                    {page.label || getPageTitle(page)}
                                  </Link>
                                </li>
                              ))}
                            </ul>
                          ) : (
                            <Text c="dimmed" size="sm">
                              Services will be available soon.
                            </Text>
                          )}

                          {hasMorePages ? (
                            <Button
                              type="button"
                              variant="subtle"
                              color="green"
                              radius="md"
                              onClick={() => toggleCategory(categoryKey)}
                              rightSection={
                                isExpanded ? (
                                  <IconChevronUp size={16} />
                                ) : (
                                  <IconChevronDown size={16} />
                                )
                              }
                              className="assignment-expand-button"
                            >
                              {isExpanded ? "Show less" : "View more services"}
                            </Button>
                          ) : null}

                          {category.cta ? (
                            <Text size="sm" c={theme.colors.green[8]} fw={700} mt="auto">
                              {category.cta}
                            </Text>
                          ) : null}
                        </Stack>
                      </Card>
                    );
                  })}
                </div>
              ) : (
                <Card withBorder radius="md" p="xl">
                  <Text fw={700}>Services will be available soon.</Text>
                </Card>
              )}
            </Stack>
          </Box>

          <AssignmentHelpHowItWorks />

          <Box
            component="section"
            aria-labelledby="assignment-types-heading"
            className="assignment-scroll-target"
            id={assignmentTypesId}
          >
            <Stack gap="lg">
              <Stack gap={6} maw={760}>
                <Title order={2} id="assignment-types-heading">
                  Assignment Types We Support
                </Title>
                <Text c="dimmed" className="assignment-section-copy">
                  Get academic guidance for common coursework formats across
                  written, technical, research, and analysis-based tasks.
                </Text>
              </Stack>
              <ul className="assignment-type-tags">
                {servicesLandingContent.assignmentTypes.map((assignmentType) => (
                  <li key={assignmentType}>{assignmentType}</li>
                ))}
              </ul>
            </Stack>
          </Box>

          <Box
            component="section"
            aria-labelledby="why-choose-subjectbuddy-heading"
            className="assignment-scroll-target"
            id={whyChooseId}
          >
            <Stack gap="lg">
              <Title order={2} id="why-choose-subjectbuddy-heading">
                Why Choose SubjectBuddy
              </Title>
              <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="lg">
                {servicesLandingContent.whyChoose.map((item) => (
                  <Card key={item.title} withBorder radius="md" p="lg" className="assignment-info-card">
                    <Stack gap="sm">
                      <ThemeIcon color="green" variant="light" radius="xl" size={38}>
                        <IconCheck size={20} />
                      </ThemeIcon>
                      <Title order={3}>{item.title}</Title>
                      <Text size="sm" c="dimmed" className="assignment-card-copy">
                        {item.description}
                      </Text>
                    </Stack>
                  </Card>
                ))}
              </SimpleGrid>
            </Stack>
          </Box>

          <Box
            component="section"
            aria-labelledby="assignment-help-faq-heading"
            className="assignment-scroll-target"
            id={faqId}
          >
            <Stack gap="lg">
              <Title order={2} id="assignment-help-faq-heading">
                Assignment Help FAQs
              </Title>
              <Accordion variant="separated" radius="md" className="assignment-main-faq">
                {servicesLandingContent.mainFaqs.map((faq) => (
                  <Accordion.Item key={faq.question} value={faq.question}>
                    <Accordion.Control>
                      <Text component="span" fw={800}>
                        {faq.question}
                      </Text>
                    </Accordion.Control>
                    <Accordion.Panel>
                      <Text c="dimmed" className="assignment-card-copy">
                        {faq.answer}
                      </Text>
                    </Accordion.Panel>
                  </Accordion.Item>
                ))}
              </Accordion>
            </Stack>
          </Box>

          <Box component="section" className="assignment-scroll-target" id={getHelpId}>
            <AssignmentSupportCta className="assignment-final-cta" />
          </Box>
        </Stack>
      </Container>
    </main>
  );
}

export default AssignmentHelpIndexPage;
